import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useOnboarding } from "../context/OnboardingContext";
import { safeRedirectTarget } from "../lib/redirectTarget";
import {
  ONBOARDING_PATH,
  ONBOARDING_STEP_COUNT,
  hasVisionContent,
  isProfileStepComplete,
  normalizeStep,
  stepNumber,
} from "../lib/onboarding";
import PlayerProfile from "./PlayerProfile";
import PlayerIdentity from "./PlayerIdentity";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { arrivalToastSuppressed } from "../lib/arrivalToastGate";

/**
 * The first-run walkthrough (issue #289): profile, then Identity & Vision,
 * then the app.
 *
 * Martin's decision, 15 Aug 2026 — "when a player registers for the first
 * time, it should direct the player to complete their profile first and then
 * complete their Identity & Vision as a player". Before this, a successful
 * signup went straight to `/dashboard`, which for an account that is four
 * seconds old has no matches, no goals and no feedback on it, and neither
 * destination was linked from anywhere a new player would look.
 *
 * ONE ROUTE, TWO STEPS, IN THE QUERY STRING (`?step=profile|identity`) —
 * the same shape `/development?tab=identity` already uses, so a step is a
 * real URL: refreshing keeps your place, and the harness reaches each step
 * without a click sequence.
 *
 * IT RENDERS FOR ANY PLAYER WHO ASKS FOR IT, finished or not. The GATE
 * (`components/OnboardingGate.jsx`) is what stops after a finish or a skip;
 * the PAGE stays reachable, which is what lets the Dashboard's resume prompt
 * link straight here after a skip without needing a second copy of these two
 * forms. Non-players never get here at all — `App.js` guards the route with
 * `roles={["player"]}`, exactly as `/statistics` does (#283), so a coach's
 * cells are role-skipped rather than walked into a redirect.
 *
 * CONTINUE SAVES. Each step drives the underlying page's own save through a
 * ref rather than asking the player to press Save and then Continue — see
 * `PlayerProfile.jsx`'s header. That is also why there is no separate "your
 * work will be lost" warning: there is no unsaved state to lose on the
 * happy path, and Skip deliberately does not save a half-filled form.
 *
 * WHERE IT HANDS OFF (#257). `location.state.from` is the destination the
 * player was originally heading for — the shared link that sent them to
 * `/register`, or the page the gate intercepted. Finish and Skip both hand
 * control back to it through the same validator every other redirect in this
 * app uses. So a deep link is deferred by two screens, never discarded.
 */
export default function Onboarding() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const location = useLocation();
  const { status, step: serverStep, applyStatus } = useOnboarding();

  // Where to go when this is over. Validated, never trusted — history state
  // is attacker-writable (see lib/redirectTarget.js).
  const target = safeRedirectTarget(location.state?.from);

  // The URL owns the step. On arrival with no `?step=` the server's answer
  // decides — that is the resume: a player who saved a profile and closed
  // the tab lands on step 2, from the marker plus the field state, with no
  // localStorage involved anywhere.
  const requestedStep = sp.get("step");
  const step = normalizeStep(requestedStep || serverStep || "profile");
  useEffect(() => {
    if (requestedStep !== step) {
      const next = new URLSearchParams(sp);
      next.set("step", step);
      setSp(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, requestedStep]);

  const profileControl = useRef(null);
  const identityControl = useRef(null);
  const [profileReady, setProfileReady] = useState(false);
  const [visionReady, setVisionReady] = useState(false);
  const [busy, setBusy] = useState(false);

  const goTo = (nextStep) =>
    nav(`${ONBOARDING_PATH}?step=${nextStep}`, { replace: true, state: location.state });

  /** Finish or skip: one bodyless POST, whichever it was (the server records
   * "asked and answered", not which button did it — see the route's note). */
  const stopAsking = async () => {
    try {
      const { data } = await api.post("/players/me/onboarding/complete");
      applyStatus(data);
      return true;
    } catch (e) {
      console.error("Onboarding: failed to record completion", e);
      toast.error("We couldn't save that just now — please try again.");
      return false;
    }
  };

  const skip = async () => {
    setBusy(true);
    const ok = await stopAsking();
    setBusy(false);
    // Only navigate on success: sending them to the dashboard with the
    // marker unwritten means the gate walks them straight back here, which
    // reads as a button that does nothing.
    if (ok) nav(target, { replace: true });
  };

  const continueFromProfile = async () => {
    setBusy(true);
    const saved = await profileControl.current?.save();
    setBusy(false);
    if (saved) goTo("identity");
  };

  const finish = async () => {
    setBusy(true);
    const saved = await identityControl.current?.save();
    if (!saved) { setBusy(false); return; }
    const ok = await stopAsking();
    setBusy(false);
    if (ok) {
      // D109 (PR #846, second review) — the new empty record already says
      // what just happened ("Nothing on it yet"); see arrivalToastGate.js
      // for why `target` alone (no fetch) is the right check here.
      if (!arrivalToastSuppressed(target)) toast.success("You're all set!");
      nav(target, { replace: true });
    }
  };

  const isProfileStep = step === "profile";
  const canContinue = isProfileStep ? profileReady : visionReady;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="onboarding-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.3em]" style={{ color: "#D4A017" }} data-testid="onboarding-step-label">
          Step {stepNumber(step)} of {ONBOARDING_STEP_COUNT}
        </p>
        <h1 className="font-display font-black text-3xl sm:text-4xl text-slate-900 mt-1">
          {isProfileStep ? "Set up your player profile" : "Your vision as a cricketer"}
        </h1>
        <p className="text-slate-500 mt-2 max-w-2xl">
          {isProfileStep
            ? "Two quick steps before you start logging. First, how you play — your role, and whether you bat, bowl or both. A photo is not required."
            : "Now the part the app is built around: where you're heading, and why. One answer is enough to start — you can come back and fill in the rest any time."}
        </p>
        <Progress
          value={(stepNumber(step) / ONBOARDING_STEP_COUNT) * 100}
          className="mt-4 h-2"
          data-testid="onboarding-progress"
        />
      </div>

      {/* Already answered, and still here — reachable from the Dashboard's
          resume prompt, or by typing the URL. Said out loud rather than
          redirected away: a player who came back deliberately should not be
          bounced, and a player who arrives by accident deserves to know
          nothing is being demanded of them. */}
      {status && !status.required && (
        <Card className="border-slate-200 bg-slate-50" data-testid="onboarding-already-done">
          <CardContent className="p-4 text-sm text-slate-600">
            You've already been through this — anything you change here saves exactly as it does on your
            profile and development pages.
          </CardContent>
        </Card>
      )}

      {isProfileStep ? (
        <PlayerProfile
          embedded
          controlRef={profileControl}
          onStateChange={(p) => setProfileReady(isProfileStepComplete(p))}
        />
      ) : (
        <PlayerIdentity
          embedded
          controlRef={identityControl}
          onVisionChange={(v) => setVisionReady(hasVisionContent(v))}
        />
      )}

      <Card className="border-slate-200">
        <CardContent className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div className="min-w-0">
            <p className="text-sm text-slate-600" data-testid="onboarding-hint">
              {canContinue
                ? isProfileStep
                  ? "That's everything we need for now."
                  : "That's enough to get started."
                : isProfileStep
                  ? "Choose a playing role, and a batting or bowling style, to continue."
                  : "Answer one question in this section to continue."}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Skip is real, and it is permanent as far as the router is
                concerned (slice 3): signup already asks for a date of birth
                and a guardian confirmation (#169), and a hard gate on top of
                that is where a parent setting up an under-11 gives up. What
                a skipper gets instead is a prompt on the dashboard — one
                card, on one page, that disappears the moment they either
                finish the fields or start using the app. */}
            <Button
              variant="ghost"
              onClick={skip}
              disabled={busy}
              data-testid="onboarding-skip"
              className="text-slate-600"
            >
              Skip for now
            </Button>
            {isProfileStep ? (
              <Button
                onClick={continueFromProfile}
                disabled={busy || !canContinue}
                data-testid="onboarding-continue"
                style={{ background: "#0B2E59" }}
              >
                {busy ? "Saving…" : "Continue"} <ArrowRight size={14} className="ml-1.5" />
              </Button>
            ) : (
              <Button
                onClick={finish}
                disabled={busy || !canContinue}
                data-testid="onboarding-finish"
                style={{ background: "#0B2E59" }}
              >
                {busy ? "Saving…" : "Finish"} <Check size={14} className="ml-1.5" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
